/**
 * BattlefieldManager — random battlefield background selector for Tap
 * Battles. Every battle picks a random environment from the library
 * (biased against immediate-repeat) so back-to-back battles feel
 * different without touching any gameplay logic.
 *
 * Design goals (per spec):
 *  - Expandable: new environments simply added to REGISTRY below.
 *  - Efficient: only preloads the selected background (lazy loading).
 *  - Extensible: entries can carry tags (biome, season, time, weather,
 *    rarity) so future filters (region-specific, seasonal, day/night,
 *    weather, legendary, event-only) can be added without a rewrite.
 *  - No hard cuts: consumers fade between backgrounds.
 */

// Static registry. When you drop a new file into
// /app/frontend/public/assets/battlefields/, add an entry here with
// whatever tags you want to filter by later.
const REGISTRY = [
  {
    id: 'rocky_riverbank_1',
    src: '/assets/battlefields/rocky_riverbank_1.webp',
    name: 'Rocky Riverbank',
    tags: { biome: 'river', season: 'summer', time: 'day', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'cypress_swamp_1',
    src: '/assets/battlefields/cypress_swamp_1.webp',
    name: 'Cypress Swamp',
    tags: { biome: 'wetlands', season: 'summer', time: 'dusk', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'meadow_1',
    src: '/assets/battlefields/meadow_1.webp',
    name: 'Wildflower Meadow',
    tags: { biome: 'meadow', season: 'summer', time: 'day', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'mountain_peak_1',
    src: '/assets/battlefields/mountain_peak_1.webp',
    name: 'Mountain Peak',
    tags: { biome: 'mountain', season: 'summer', time: 'dusk', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'cypress_swamp_2',
    src: '/assets/battlefields/cypress_swamp_2.webp',
    name: 'Mystic Bayou',
    tags: { biome: 'wetlands', season: 'summer', time: 'dusk', weather: 'fog', rarity: 'rare' },
  },
  {
    id: 'conifer_falls_1',
    src: '/assets/battlefields/conifer_falls_1.webp',
    name: 'Conifer Falls',
    tags: { biome: 'forest', season: 'summer', time: 'day', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'mountain_temple_1',
    src: '/assets/battlefields/mountain_temple_1.webp',
    name: 'Sunmaker Temple',
    tags: { biome: 'mountain', season: 'summer', time: 'day', weather: 'clear', rarity: 'legendary' },
  },
  {
    id: 'haunted_swamp_1',
    src: '/assets/battlefields/haunted_swamp_1.webp',
    name: 'Haunted Bayou',
    tags: { biome: 'wetlands', season: 'summer', time: 'dusk', weather: 'fog', rarity: 'rare' },
  },
  {
    id: 'mountain_peak_2',
    src: '/assets/battlefields/mountain_peak_2.webp',
    name: 'Skyward Ridge',
    tags: { biome: 'mountain', season: 'summer', time: 'day', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'valley_meadow_1',
    src: '/assets/battlefields/valley_meadow_1.webp',
    name: 'Lakeside Meadow',
    tags: { biome: 'meadow', season: 'summer', time: 'dusk', weather: 'clear', rarity: 'common' },
  },
  // ---- iter58: nano-banana generated arenas ----
  {
    id: 'tundra_1',
    src: '/assets/battlefields/tundra_1.webp',
    name: 'Arctic Tundra',
    tags: { biome: 'tundra', season: 'winter', time: 'day', weather: 'snow', rarity: 'common' },
  },
  {
    id: 'desert_1',
    src: '/assets/battlefields/desert_1.webp',
    name: 'Red Mesa Basin',
    tags: { biome: 'desert', season: 'summer', time: 'dusk', weather: 'clear', rarity: 'common' },
  },
  {
    id: 'coastal_cliff_1',
    src: '/assets/battlefields/coastal_cliff_1.webp',
    name: 'Storm Cliff Overlook',
    tags: { biome: 'coast', season: 'summer', time: 'day', weather: 'wind', rarity: 'rare' },
  },
  {
    id: 'night_forest_1',
    src: '/assets/battlefields/night_forest_1.webp',
    name: 'Moonlit Grove',
    tags: { biome: 'forest', season: 'summer', time: 'night', weather: 'clear', rarity: 'rare' },
  },
  {
    id: 'alpine_lake_1',
    src: '/assets/battlefields/alpine_lake_1.webp',
    name: 'Mirror Lake',
    tags: { biome: 'lake', season: 'summer', time: 'dawn', weather: 'fog', rarity: 'common' },
  },
];

const LAST_KEY = 'wq_last_battlefield_id';

// Seasonal grading — pushes the same summer photograph into autumn or
// winter palettes so a battle in November feels visibly different from
// one in July. Northern-hemisphere calendar since that's where most of
// the WildQuest player base lives; if we need per-user hemispheres
// later we can pass a hint into getRandomBattlefield.
const SEASON_FILTERS = {
  summer: { css: 'saturate(0.95)',                       tint: null },
  autumn: { css: 'saturate(1.15) hue-rotate(-14deg) sepia(0.18) brightness(0.95)',
            tint: 'rgba(120, 60, 20, 0.16)' },
  winter: { css: 'saturate(0.45) hue-rotate(15deg) brightness(0.85) contrast(1.05)',
            tint: 'rgba(140, 170, 200, 0.20)' },
  spring: { css: 'saturate(1.10) hue-rotate(6deg) brightness(1.02)',
            tint: null },
};

/** Northern-hemisphere season for the current calendar month. */
export function getCurrentSeason(now = new Date()) {
  const m = now.getMonth(); // 0=Jan
  if (m >= 2 && m <= 4)  return 'spring'; // Mar-May
  if (m >= 5 && m <= 7)  return 'summer'; // Jun-Aug
  if (m >= 8 && m <= 10) return 'autumn'; // Sep-Nov
  return 'winter';                        // Dec-Feb
}

/** CSS filter string + tint colour for a given season. */
export function seasonalGrading(season) {
  return SEASON_FILTERS[season] || SEASON_FILTERS.summer;
}

// In-memory image cache — keeps decoded frames warm so a re-selection
// during the same session is instant.
const _cache = new Map(); // id -> HTMLImageElement (or Image ready flag)

function _getLastId() {
  try { return localStorage.getItem(LAST_KEY); } catch (_e) { return null; }
}
function _setLastId(id) {
  try { localStorage.setItem(LAST_KEY, id); } catch (_e) { /* non-fatal */ }
}

/**
 * Return the full battlefield library.
 * @param {object} [filters] optional tag filters, e.g. {biome:'wetlands'}
 */
export function loadBattlefields(filters) {
  if (!filters) return REGISTRY.slice();
  return REGISTRY.filter(b =>
    Object.entries(filters).every(([k, v]) => b.tags?.[k] === v),
  );
}

/**
 * Pick a random battlefield, biased against picking the same id as the
 * last battle (unless there's only one option). `filters` narrows the
 * candidate pool by tag equality. The returned entry is augmented with
 * `season` + `grading` fields derived from the current calendar month
 * so callers can apply a consistent seasonal look.
 */
export function getRandomBattlefield(filters, { season: seasonOverride } = {}) {
  const pool = loadBattlefields(filters);
  if (pool.length === 0) return null;
  const lastId = _getLastId();
  let pick;
  if (pool.length === 1) {
    pick = pool[0];
  } else {
    const candidates = pool.filter(b => b.id !== lastId);
    const source = candidates.length > 0 ? candidates : pool;
    pick = source[Math.floor(Math.random() * source.length)];
    _setLastId(pick.id);
  }
  const season = seasonOverride || getCurrentSeason();
  return { ...pick, season, grading: seasonalGrading(season) };
}

/**
 * Preload a single battlefield asset. Resolves once the browser has
 * decoded the image so the caller can fade it in without a flash.
 */
export function preloadSelected(battlefield) {
  if (!battlefield) return Promise.resolve(null);
  if (_cache.has(battlefield.id)) return Promise.resolve(_cache.get(battlefield.id));
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => { _cache.set(battlefield.id, img); resolve(img); };
    img.onerror = () => { resolve(null); };
    img.src = battlefield.src;
  });
}

/**
 * Warm the cache for the previous battle's background so returning to
 * the same environment (e.g. a rematch) is instant. Consumers can call
 * this after the current battle resolves.
 */
export function cachePrevious(battlefield) {
  if (!battlefield) return;
  if (_cache.has(battlefield.id)) return;
  const img = new Image();
  img.onload = () => _cache.set(battlefield.id, img);
  img.src = battlefield.src;
}

/**
 * Convenience: pick + preload in one call. Returns the selected
 * battlefield entry (the promise resolves once the asset is decoded).
 */
export async function pickAndPreload(filters) {
  const picked = getRandomBattlefield(filters);
  await preloadSelected(picked);
  return picked;
}

/** Called by cleanup logic when leaving a battle scene. Currently a no-op
 * — kept in the API surface so a future LRU eviction can slot in without
 * touching call sites. */
export function evictStale() { /* future: prune _cache when > N entries */ }

export const BattlefieldManager = {
  loadBattlefields,
  getRandomBattlefield,
  preloadSelected,
  cachePrevious,
  pickAndPreload,
  evictStale,
  getCurrentSeason,
  seasonalGrading,
  registry: REGISTRY, // read-only reference for tooling
};

export default BattlefieldManager;
